import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import ApexChartLoader from "../../../common-components/loaders/ApexChartLoader";

const WeightProfileChart = () => {
  const { memberWeight, memberWeightError, memberWeightLoading } = useSelector(
    (state) => state.prospects
  );

  const [weightStats, setWeightStats] = useState([]);
  const [chartData, setChartData] = useState({
    categories: [],
    data: [],
    yAxisMin: 0,
    yAxisMax: 100,
  });

  useEffect(() => {
    if (
      !memberWeightError &&
      !memberWeightLoading &&
      memberWeight?.status &&
      memberWeight?.data
    ) {
      setWeightStats(memberWeight.data?.[0]?.stats || []);
    } else {
      setWeightStats([]);
    }
  }, [memberWeight, memberWeightError, memberWeightLoading]);

  useEffect(() => {
    if (weightStats && weightStats.length > 0) {
      const sortedStats = [...weightStats].sort(
        (a, b) => {
          const dateA = parseInt(a.date_added) || 0;
          const dateB = parseInt(b.date_added) || 0;
          return dateA - dateB;
        }
      );

      const validStats = sortedStats.filter((stat) => {
        const weightStr = stat.weight?.replace("_kg", "") || "0";
        const weight = parseFloat(weightStr);
        return !isNaN(weight) && isFinite(weight);
      });

      if (validStats.length === 0) {
        setChartData({
          categories: [],
          data: [],
          yAxisMin: 0,
          yAxisMax: 100,
        });
        return;
      }

      const categories = validStats.map((stat) => stat.date || "NA");
      const weights = validStats.map((stat) => {
        const weightStr = stat.weight?.replace("_kg", "") || "0";
        return parseFloat(weightStr);
      });

      const minWeight = Math.min(...weights);
      const maxWeight = Math.max(...weights);
      const padding = (maxWeight - minWeight) * 0.1 || 5;

      setChartData({
        categories,
        data: weights,
        yAxisMin: Math.floor(minWeight - padding),
        yAxisMax: Math.ceil(maxWeight + padding),
      });
    }
  }, [weightStats]);

  const weightChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    colors: ["#3b82f6"],
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: { colors: "#6b7280", fontSize: "12px" },
      },
    },
    yaxis: {
      min: chartData.yAxisMin,
      max: chartData.yAxisMax,
      labels: {
        style: { colors: "#6b7280", fontSize: "12px" },
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
    markers: {
      size: 6,
      colors: ["#fff"],
      strokeColors: ["#3b82f6"],
      strokeWidth: 2,
      hover: { size: 8 },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "16px",
      fontWeight: 400,
      fontFamily: "inherit",
      offsetY: 10,
      markers: {
        width: 10,
        height: 10,
        strokeWidth: 0,
        strokeColor: "#3b82f6",
        fillColors: ["#3b82f6"],
        radius: 12,
        offsetX: -5,
      },
      itemMargin: {
        horizontal: 0,
        vertical: 0,
      },
      labels: {
        colors: ["#3b82f6"],
        useSeriesColors: false,
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val) => `${val} kg`,
      },
    },
  };

  const weightChartSeries = [
    {
      name: "Weight (kg)",
      data: chartData.data,
    },
  ];

  return (
    <div className="tme-analysis-chart-comparison-section">
      <h3 className="tme-analysis-chart-section-title">
        Weight Profile Comparison (Kg)
      </h3>
      <div className="tme-analysis-chart-chart-container">
        {memberWeightLoading ? (
          <ApexChartLoader />
        ) : (
          <ReactApexChart
            options={weightChartOptions}
            series={weightChartSeries}
            type="line"
            height={300}
          />
        )}
      </div>
    </div>
  );
};

export default WeightProfileChart;
